import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Globe, Menu, X, FileDown } from "lucide-react";
import { useLanguage } from "../../LanguageContext";
import logo from "../../images/LogoColor.svg";

const NavBar = () => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  const toggleMenu = () => setIsOpen(!isOpen);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { id: "home", en: "Home", fr: "Accueil", href: "/", type: "route" },
    { id: "story", en: "Story", fr: "Histoire", href: "/#story", type: "anchor" },
    { id: "process", en: "Our History", fr: "Notre Histoire", href: "/process", type: "route" },
    { id: "products", en: "Products", fr: "Produits", href: "/products", type: "route" },
    { id: "blog", en: "Blog", fr: "Blog", href: "/blog", type: "route" },
    { id: "contact", en: "Contact", fr: "Contact", href: "/contact", type: "route" },
  ];

  const scrollToSection = (href) => {
    if (href.startsWith("/#")) {
      const id = href.split("#")[1];
      const element = document.getElementById(id);
      const navbar = document.querySelector("nav");
      if (element && navbar) {
        const navbarHeight = navbar.offsetHeight;
        const extraOffset = 15;
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - navbarHeight - extraOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    } else {
      navigate(href);
    }
    setIsOpen(false);
  };

  const leftItems = navItems.slice(0, 3);
  const rightItems = navItems.slice(3);

  return (
    <nav
      className={`fixed top-0 w-full z-30 transition-all duration-500 ${
        isScrolled ? "bg-white/95 backdrop-blur-md border-0 shadow-none" : "bg-red border-0 shadow-none"
      }`}
    >
      <div className="flex items-center h-20 px-6 max-w-7xl mx-auto relative">
        {/* Left Navigation - Desktop Only */}
        <div className="hidden lg:flex flex-1 justify-end space-x-12">
          {leftItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.href)}
              className="relative text-gray-700 hover:text-green-700 text-base font-medium transition-all duration-300 group"
              style={{ fontFamily: "Inter18Light" }}
            >
              {language === "en" ? item.en : item.fr}
            </button>
          ))}
        </div>

        {/* Center Logo - Centered on mobile, part of flex on desktop */}
        <div className="absolute left-1/2 transform -translate-x-1/2 lg:relative lg:left-auto lg:transform-none lg:flex-1 lg:flex lg:justify-center mt-1">
          <img
            src={logo}
            alt="Logo"
            className="h-40 w-auto object-contain cursor-pointer"
            onClick={() => scrollToSection("/")}
          />
        </div>

        {/* Right Navigation - Desktop Only */}
        <div className="hidden lg:flex flex-1 justify-start space-x-6 items-center">
          {rightItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.href)}
              className="relative text-gray-700 hover:text-green-700 text-base font-medium transition-all duration-300 group"
              style={{ fontFamily: "Inter18Light" }}
            >
              {language === "en" ? item.en : item.fr}
            </button>
          ))}
          <a
            href="/Solivera.pdf"
            download="Solivera.pdf"
            className="relative inline-flex items-center px-3.5 py-1.5 bg-gradient-to-r from-[#1b4600] via-[#246b00] to-[#2d7a00] hover:from-[#246b00] hover:via-[#2d7a00] hover:to-[#3a8f00] rounded-md text-xs text-white font-semibold transition-all duration-500 shadow-md hover:shadow-lg hover:shadow-green-900/30 ml-4"
            style={{ fontFamily: "Inter18Light" }}
          >
            <FileDown size={14} className="mr-2" />
            <span>{language === "en" ? "Download Brochure" : "Télécharger la Brochure"}</span>
          </a>
        </div>

        {/* Mobile Menu Toggle - Positioned absolutely on the right */}
        <div className="lg:hidden absolute right-6 top-1/2 transform -translate-y-1/2 z-50">
          <button onClick={toggleMenu} className="text-gray-800 focus:outline-none">
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {isOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-md shadow-md absolute top-20 left-0 w-full flex flex-col items-center py-4 space-y-4">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.href)}
                className="text-gray-700 hover:text-green-700 text-lg font-medium"
                style={{ fontFamily: "Inter18Light" }}
              >
                {language === "en" ? item.en : item.fr}
              </button>
            ))}
            <a
              href="/Solivera.pdf"
              download="Solivera.pdf"
              className="relative inline-flex items-center px-3.5 py-1.5 bg-gradient-to-r from-[#1b4600] via-[#246b00] to-[#2d7a00] hover:from-[#246b00] hover:via-[#2d7a00] hover:to-[#3a8f00] rounded-md text-xs text-white font-semibold transition-all duration-500 shadow-md hover:shadow-lg hover:shadow-green-900/30 mt-2"
              style={{ fontFamily: "Inter18Light" }}
            >
              <FileDown size={14} className="mr-2" />
              <span>{language === "en" ? "Download Brochure" : "Télécharger la Brochure"}</span>
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavBar;